import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import {
  AgentEvalIncorrectOutcome,
  AgentEvalRun,
  AgentEvalTestResult,
} from '../agent-evals.types';
import { ClientProfileComponent } from '../../view-project-new/client-profile/client-profile.component';

export interface AgentEvalReportDialogData {
  run: AgentEvalRun;
  projectDetails: any;
  projectName?: string;
}

@Component({
  selector: 'hour4u-admin-agent-eval-report-dialog',
  templateUrl: './report-dialog.component.html',
  styleUrls: ['./report-dialog.component.scss'],
})
export class AgentEvalReportDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<AgentEvalReportDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: AgentEvalReportDialogData
  ) {}

  get run(): AgentEvalRun {
    return (this.data && this.data.run) || ({} as AgentEvalRun);
  }

  get results(): AgentEvalTestResult[] {
    return (this.run && this.run.results) || [];
  }

  get incorrectOutcomes(): Array<AgentEvalIncorrectOutcome & { test_name?: string }> {
    const seen = new Set<string>();
    const out: Array<AgentEvalIncorrectOutcome & { test_name?: string }> = [];
    for (const r of this.results) {
      for (const o of r.incorrect_outcomes || []) {
        if (!this.isRealConflict(o)) {
          continue;
        }
        const key = (o.sample_key || o.client_number || o.client_name || '').toString();
        if (key && seen.has(key)) {
          continue;
        }
        if (key) {
          seen.add(key);
        }
        out.push({ ...o, test_name: r.name });
      }
    }
    return out;
  }

  get summary(): any {
    return (this.run && this.run.summary) || {};
  }

  projectTitle(): string {
    return (
      (this.data && this.data.projectName) ||
      (this.data &&
        this.data.projectDetails &&
        this.data.projectDetails.project_name) ||
      'Project'
    );
  }

  finishedLabel(): string {
    const raw = this.run.finished_at || this.run.started_at || '';
    return this.formatIst(raw);
  }

  /** Format ISO timestamps in Asia/Kolkata (IST). */
  formatIst(iso: string): string {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return iso;
    }
    try {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(d) + ' IST';
    } catch (e) {
      return iso;
    }
  }

  isRealConflict(o: AgentEvalIncorrectOutcome): boolean {
    const ai = ((o && (o.outcome_by_ai || o.recorded_status)) || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const suggested = (
      (o && (o.eval_suggested_outcome || o.suggested_status)) ||
      ''
    )
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    // Human≠AI alone is not an incorrect outcome (comparison only).
    if (!ai) {
      return true;
    }
    if (suggested && suggested !== ai) {
      return true;
    }
    return false;
  }

  openClientHistory(o: AgentEvalIncorrectOutcome): void {
    const peopleId = this.peopleIdFromOutcome(o);
    const clientNumber = String((o && o.client_number) || '').trim();
    const projectId =
      (this.data &&
        this.data.projectDetails &&
        (this.data.projectDetails.project_id || this.data.projectDetails.projectId)) ||
      this.run.project_id ||
      '';
    if (!projectId || (!peopleId && !clientNumber)) {
      return;
    }
    const callDetails = {
      people_id: peopleId || '',
      project_id: projectId,
      client_number: clientNumber,
      client_name: (o && o.client_name) || '',
      status: (o && o.overall_outcome) || o.outcome_by_ai || '',
    };
    this.dialog.open(ClientProfileComponent, {
      disableClose: true,
      width: '45%',
      height: '100vh',
      position: { right: '0', top: '0', bottom: '0' },
      panelClass: ['add-edit-role-dialog', 'client-profile-panel'],
      data: {
        projectDetails: this.data.projectDetails,
        projectId,
        callDetails,
        selectedTabIndex: 0,
        hideLiveTranscription: true,
      },
      autoFocus: true,
    });
  }

  peopleIdFromOutcome(o: AgentEvalIncorrectOutcome): string {
    const direct = ((o as any) && (o as any).people_id) || '';
    if (direct) {
      return String(direct);
    }
    const key = ((o && o.sample_key) || '').toString();
    if (key.indexOf('p:') === 0 && key.length > 2) {
      return key.slice(2);
    }
    return '';
  }

  close(): void {
    this.dialogRef.close();
  }
}
